
import { useEffect, useRef } from "react";
import { useAuthSessionInit } from "./auth/useAuthSessionInit";
import { useAdminStatus } from "./auth/useAdminStatus";
import { useProfileFetch } from "./auth/useProfileFetch";
import { useAuthStabilization } from "./auth/useAuthStabilization";
import { useSecurityRealtimeGuard } from "./auth/useSecurityRealtimeGuard";
import { AuthStateType } from "./types/auth-state";

export function useAuthSession(authState: AuthStateType, restoreImpersonation?: () => Promise<void>) {
  const restorationAttemptedRef = useRef(false);

  // Initialize session and auth state listener
  useAuthSessionInit(authState);
  
  // Handle admin status checks
  useAdminStatus(authState);
  
  // Handle profile fetching and get auth stability status
  const { isAuthStable, refreshProfile } = useProfileFetch(authState);
  
  // Add refreshProfile to auth state
  authState.refreshProfile = refreshProfile;
  
  // Handle auth stabilization to prevent race conditions
  const { isAuthReady } = useAuthStabilization(authState);
  
  // Realtime guard: force logout on suspension or company removal
  useSecurityRealtimeGuard(authState);

  // Restore impersonation after initial auth is stable
  useEffect(() => {
    if (isAuthStable && restoreImpersonation && !restorationAttemptedRef.current && !authState.isImpersonating) {
      restorationAttemptedRef.current = true;
      restoreImpersonation();
    }
  }, [isAuthStable, restoreImpersonation, authState.isImpersonating]);
  
  return {
    isAuthStable,
    isAuthReady
  };
}
